import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHeart, FaComment, FaShare, FaBookmark, FaLock, FaChartLine, FaDollarSign, FaTrash } from 'react-icons/fa';
import VideoPlayer from './VideoPlayer';
import CommentSection from './CommentSection';
import PurchaseModal from './PurchaseModal';
import { getCurrentUser } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface VideoReelProps {
  video: {
    id: string;
    url: string;
    poster?: string;
    user: {
      id: string;
      name: string;
      avatar: string;
      email?: string;
      username?: string;
    };
    description: string;
    likes: number;
    comments: number;
    shares: number;
    isLiked: boolean;
    isSaved: boolean;
    category: string;
    language: string;
    is_paid: boolean;
    is_promoted: boolean;
    price: number;
  };
  onLike: (id: string) => void;
  onComment: (id: string) => void;
  isActive: boolean;
  isOwner: boolean;
}

const VideoReel: React.FC<VideoReelProps> = ({
  video,
  onLike,
  onComment,
  isActive,
  isOwner: propIsOwner
}) => {
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isOwner, setIsOwner] = useState(propIsOwner);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const user = getCurrentUser();

  useEffect(() => {
    checkVideoOwnership();
    loadLikeData();
  }, [video, user]);

  const checkVideoOwnership = async () => {
    if (!user || !video.user) return;

    // Check if any of these match:
    // 1. User ID matches
    // 2. Email matches
    // 3. Username matches
    const isOwner = 
      user.id === video.user.id ||
      user.email === video.user.email ||
      user.username === video.user.username;

    setIsOwner(isOwner);
  };

  const loadLikeData = async () => {
    try {
      // Get total likes count
      const { count: totalLikes } = await supabase
        .from('ponovideo_likes')
        .select('*', { count: 'exact', head: true })
        .eq('video_id', video.id);

      setLikesCount(totalLikes || 0);

      // Check if current user has liked
      if (user) {
        const { data: likeData } = await supabase
          .from('ponovideo_likes')
          .select('id')
          .eq('video_id', video.id)
          .eq('user_id', user.id)
          .maybeSingle();

        setIsLiked(!!likeData);
      }
    } catch (error) {
      console.error('Error loading like data:', error);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Please log in to like videos');
      return;
    }

    try {
      if (isLiked) {
        // Unlike the video
        const { error: unlikeError } = await supabase
          .from('ponovideo_likes')
          .delete()
          .eq('video_id', video.id)
          .eq('user_id', user.id);

        if (unlikeError) throw unlikeError;

        setIsLiked(false);
        setLikesCount(prev => Math.max(0, prev - 1));

        // Update video likes count
        await supabase
          .from('ponavideos')
          .update({ likes_count: likesCount - 1 })
          .eq('id', video.id);

      } else {
        // Like the video
        const { error: likeError } = await supabase
          .from('ponovideo_likes')
          .insert({
            video_id: video.id,
            user_id: user.id
          });

        if (likeError) throw likeError;

        setIsLiked(true);
        setLikesCount(prev => prev + 1);

        // Update video likes count
        await supabase
          .from('ponavideos')
          .update({ likes_count: likesCount + 1 })
          .eq('id', video.id);
      }

      // Call the onLike callback
      onLike(video.id);
    } catch (error: any) {
      console.error('Error toggling like:', error);
      toast.error('Failed to update like');
      
      // Revert optimistic update on error
      await loadLikeData();
    }
  };

  const handleVideoClick = () => {
    if (video.is_paid && !user?.is_admin && !isOwner) {
      setShowPurchaseModal(true);
    }
  };

  const handleShare = async () => {
    try {
      const shareUrl = `${window.location.origin}/video/${video.id}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Error sharing video:', error);
      toast.error('Failed to share video');
    }
  };

  const handleSetPrice = async () => {
    if (!user) {
      toast.error('Please log in to set video price');
      return;
    }

    if (!isOwner) {
      toast.error('You can only monetize your own videos');
      return;
    }

    try {
      const price = window.prompt('Enter price in dollars:', video.price?.toString() || '0');
      if (price === null) return;

      const numPrice = parseFloat(price);
      if (isNaN(numPrice) || numPrice < 0) {
        toast.error('Please enter a valid price');
        return;
      }

      const { error } = await supabase
        .from('ponavideos')
        .update({ 
          is_paid: numPrice > 0,
          price: numPrice
        })
        .eq('id', video.id);

      if (error) throw error;
      toast.success(numPrice > 0 ? `Price set to $${numPrice}` : 'Video set to free');
      window.location.reload();
    } catch (error: any) {
      console.error('Error setting price:', error);
      toast.error('Failed to update video price');
    }
  };

  const handleDelete = async () => {
    if (!user || !isOwner) return;

    try {
      // Delete video from storage
      const { error: storageError } = await supabase.storage
        .from('videospono')
        .remove([`${user.id}/${video.id}.mp4`]);

      if (storageError) throw storageError;

      // Delete video record from database
      const { error: dbError } = await supabase
        .from('ponavideos')
        .delete()
        .eq('id', video.id);

      if (dbError) throw dbError;

      toast.success('Video deleted successfully');
      window.location.reload();
    } catch (error: any) {
      console.error('Error deleting video:', error);
      toast.error(error.message || 'Failed to delete video');
    }
  };

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent video click handler from firing
    navigate(`/user/${video.user.id}/videos`);
  };

  return (
    <div className="relative h-screen w-full bg-black overflow-hidden">
      <div onClick={handleVideoClick} className="h-full">
        <VideoPlayer
          src={video.url}
          poster={video.poster}
          isActive={isActive}
          isPaid={video.is_paid && !isOwner}
        />
      </div>

      {/* User Info and Description */}
      <div className="absolute bottom-20 left-3 right-[4.5rem] z-10">
        <div className="flex items-center mb-3">
          <div className="flex-shrink-0">
            <img
              src={video.user.avatar}
              alt=""
              className="w-12 h-12 rounded-full border-2 border-white cursor-pointer"
              onClick={handleUserClick}
            />
          </div>
          <div className="ml-3">
            <h3 
              className="text-white font-semibold text-lg cursor-pointer hover:text-gray-300 transition-colors"
              onClick={handleUserClick}
            >
              {video.user.name}
            </h3>
            <div className="flex items-center space-x-2 text-sm text-gray-300">
              <span>{video.category}</span>
              <span>•</span>
              <span>{video.language}</span>
            </div>
          </div>
        </div>
        <p className="text-white text-sm leading-relaxed line-clamp-2 ml-1">
          {video.description}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="absolute bottom-20 right-2 z-10 flex flex-col items-center space-y-6">
        {/* Like Button */}
        <button
          onClick={handleLike}
          className="flex flex-col items-center"
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            isLiked ? 'bg-red-500' : 'bg-black bg-opacity-50'
          } transition-colors duration-200`}>
            <FaHeart className="w-6 h-6 text-white" />
          </div>
          <span className="text-white text-xs mt-1.5">{likesCount}</span>
        </button>

        {/* Comment Button */}
        <button
          onClick={() => setShowComments(true)}
          className="flex flex-col items-center"
        >
          <div className="w-12 h-12 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
            <FaComment className="w-6 h-6 text-white" />
          </div>
          <span className="text-white text-xs mt-1.5">{video.comments}</span>
        </button>

        {/* Share Button */}
        <button
          onClick={handleShare}
          className="flex flex-col items-center"
        >
          <div className="w-12 h-12 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
            <FaShare className="w-6 h-6 text-white" />
          </div>
          <span className="text-white text-xs mt-1.5">{video.shares}</span>
        </button>

        {/* Monetize Button */}
        <button
          onClick={handleSetPrice}
          className="flex flex-col items-center"
          title={isOwner ? 'Set video price' : 'You can only monetize your own videos'}
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            isOwner ? 'bg-black bg-opacity-50 hover:bg-opacity-75' : 'bg-gray-700'
          }`}>
            <FaDollarSign className="w-6 h-6 text-white" />
          </div>
          <span className="text-white text-xs mt-1.5">
            {video.is_paid ? `$${video.price}` : 'Price'}
          </span>
        </button>

        {/* Delete Button - Only show for video owner */}
        {isOwner && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex flex-col items-center"
          >
            <div className="w-12 h-12 bg-red-600 bg-opacity-50 rounded-full flex items-center justify-center hover:bg-opacity-75">
              <FaTrash className="w-6 h-6 text-white" />
            </div>
            <span className="text-white text-xs mt-1.5">Delete</span>
          </button>
        )}
      </div>

      {/* Comments Section */}
      {showComments && (
        <CommentSection
          videoId={video.id}
          isVisible={showComments}
          onCommentAdded={() => onComment(video.id)}
          onClose={() => setShowComments(false)}
        />
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <PurchaseModal
          videoId={video.id}
          price={video.price}
          onClose={() => setShowPurchaseModal(false)}
          onPurchaseComplete={() => {
            setShowPurchaseModal(false);
            window.location.reload();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">Delete Video?</h3>
            <p className="text-gray-400 mb-6">
              Are you sure you want to delete this video? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoReel;